import { fg } from '@atlassian/jira-feature-gating';
import { localStorage, VIEW_MODE_STORAGE_KEY } from './localStorage.constants.tsx';
import { getChildIssuesLimit as getChildIssuesLimitSelector } from './getChildIssuesLimit.tsx';
import type { StoreApi, IssueContext } from './IssueContextServiceActions.types.tsx';
import { getViewModeFromLocalStorage } from './getViewModeFromLocalStorage.tsx';

export const setIssueContext =
	(issueContext: IssueContext) =>
	({ setState, getState }: StoreApi): void => {
		const { issueKey: currentIssueKey, issueId: currentIssueId } = getState();
		const currentState = getState();

		// ARKEN-792: reset issueId on issueKey change if issueId isn't provided
		if (
			currentIssueId &&
			currentIssueKey &&
			!issueContext.issueId &&
			issueContext.issueKey !== currentIssueKey
		) {
			setState({
				...issueContext,
				issueId: undefined,
				viewMode: issueContext.viewMode || currentState.viewMode,
			});
		} else {
			setState({
				...issueContext,
				viewMode: issueContext.viewMode || currentState.viewMode,
			});
		}
	};

const mergeIssueContext =
	(partialIssueContext: Partial<IssueContext>) =>
	({ getState, setState }: StoreApi): void => {
		const { issueKey: currentIssueKey, issueId: currentIssueId } = getState();

		// ARKEN-792: reset issueId on issueKey change if issueId isn't provided
		if (
			currentIssueId &&
			currentIssueKey &&
			!partialIssueContext.issueId &&
			partialIssueContext.issueKey &&
			partialIssueContext.issueKey !== currentIssueKey
		) {
			setState({ ...getState(), ...partialIssueContext, issueId: undefined });
		} else {
			setState({ ...getState(), ...partialIssueContext });
		}
	};

const getChildIssuesLimit =
	() =>
	({ getState }: StoreApi): number =>
		getChildIssuesLimitSelector(getState());

const getMaxTokenLength =
	() =>
	({ getState }: StoreApi): number =>
		getState().maxTokenLength;

const getIsLoadedWithPage =
	() =>
	({ getState }: StoreApi): boolean =>
		getState().isLoadedWithPage;

const getBackButtonUrl =
	() =>
	({ getState }: StoreApi): string | undefined =>
		getState().backButtonUrl;

const getIsSpaEnabled =
	() =>
	({ getState }: StoreApi): boolean =>
		getState().isSpaEnabled;

const getIssueKey =
	() =>
	({ getState }: StoreApi): string =>
		getState().issueKey;

const getIsArchived =
	() =>
	({ getState }: StoreApi): boolean | undefined =>
		getState().isArchived;

const setDetailViewAsModal =
	(willSave = true) =>
	({ setState }: StoreApi): void => {
		if (willSave) {
			if (fg('jira_wiv_view_mode_strictly_necessary_storage')) {
				localStorage.setStrictlyNecessaryItem(VIEW_MODE_STORAGE_KEY, 'MODAL');
			} else {
				localStorage.set(VIEW_MODE_STORAGE_KEY, 'MODAL');
			}
		}
		setState({
			viewMode: 'MODAL',
		});
	};

const setDetailViewAsSidebar =
	(willSave = true) =>
	({ setState }: StoreApi): void => {
		if (willSave) {
			if (fg('jira_wiv_view_mode_strictly_necessary_storage')) {
				localStorage.setStrictlyNecessaryItem(VIEW_MODE_STORAGE_KEY, 'SIDEBAR');
			} else {
				localStorage.set(VIEW_MODE_STORAGE_KEY, 'SIDEBAR');
			}
		}
		setState({
			viewMode: 'SIDEBAR',
		});
	};

const resetDetailView =
	() =>
	({ setState }: StoreApi): void => {
		setState({
			viewMode: getViewModeFromLocalStorage(),
		});
	};

/**
 * a special action to "sync" stores
 * @deprecated internal use only
 */
const internalSyncStore =
	(issueContext: IssueContext) =>
	({ setState }: StoreApi): void => {
		setState(issueContext);
	};

export const actions: {
	readonly internalSyncStore: (issueContext: IssueContext) => ({ setState }: StoreApi) => void;
	readonly setIssueContext: (
		issueContext: IssueContext,
	) => ({ setState, getState }: StoreApi) => void;
	readonly mergeIssueContext: (
		partialIssueContext: Partial<IssueContext>,
	) => ({ getState, setState }: StoreApi) => void;
	readonly getChildIssuesLimit: () => ({ getState }: StoreApi) => number;
	readonly getMaxTokenLength: () => ({ getState }: StoreApi) => number;
	readonly getIsLoadedWithPage: () => ({ getState }: StoreApi) => boolean;
	readonly getBackButtonUrl: () => ({ getState }: StoreApi) => string | undefined;
	readonly getIsSpaEnabled: () => ({ getState }: StoreApi) => boolean;
	readonly getIssueKey: () => ({ getState }: StoreApi) => string;
	readonly getIsArchived: () => ({ getState }: StoreApi) => boolean | undefined;
	readonly setDetailViewAsModal: (willSave?: boolean) => ({ setState }: StoreApi) => void;
	readonly setDetailViewAsSidebar: (willSave?: boolean) => ({ setState }: StoreApi) => void;
	readonly resetDetailView: () => ({ setState }: StoreApi) => void;
} = {
	internalSyncStore,
	setIssueContext,
	mergeIssueContext,
	getChildIssuesLimit,
	getMaxTokenLength,
	getIsLoadedWithPage,
	getBackButtonUrl,
	getIsSpaEnabled,
	getIssueKey,
	getIsArchived,
	setDetailViewAsModal,
	setDetailViewAsSidebar,
	resetDetailView,
} as const;
